// PickPointsDialog.tsx — Up to 8 crane pick insert locations (ppvar)

import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { useShallowStore } from '../store/useStore';
import { EmbedPalette } from '../utils/embedLibrary';
import type { EmbedTypeEntry } from '../utils/embedLibrary';
import type { PpData } from '../types/panelvar';

const FIELDS = [
  { key: 'mark', label: 'Mark', type: 'text'   as const },
  { key: 'x',    label: 'X',    type: 'number' as const, unit: '"', min: 0 },
  { key: 'y',    label: 'Y',    type: 'number' as const, unit: '"', min: 0 },
  { key: 'size', label: 'Size', type: 'number' as const, unit: '"', min: 0, step: 0.125 },
  { key: 'load', label: 'Load', type: 'number' as const, unit: 'kips', min: 0, step: 0.1 },
];

const PP_DEFAULT: PpData = { mark: 'PP', x: 0, y: 0, embedLen: 12, capacity: 5, size: 1.25, load: 0 };

export default function PickPointsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<PpData>('ppvar', panel?.ppvar ?? [],
    (px, py, mark) => ({ mark, x: px, y: py }),
    PP_DEFAULT);
  if (!panel) return null;

  function addFromPalette(entry: EmbedTypeEntry) {
    const data: PpData = { ...PP_DEFAULT, ...(entry.data as Partial<PpData>) };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 8) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  return (
    <DialogBase title="Pick Points (8 max)" onOk={() => updatePanel(panel.id, { ppvar: slots })}>
      <EmbedPalette category="pick" settings={project?.settings} onSelect={addFromPalette} />
      <SlotEditor
        slots={slots}
        fields={FIELDS}
        maxSlots={8}
        onChange={v => setSlots(v)}
        defaultData={() => ({ mark: 'PP', x: 0, y: 0, embedLen: 12, capacity: 5, size: 1.25, load: 0 })}
        label="PP"
      />
    </DialogBase>
  );
}
